import {
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Folder,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { MarkdownContent } from '@/components/markdown/markdown-content'
import { useOutlineExpansion } from '@/features/projects/use-outline-expansion'
import { useStepDone } from '@/features/projects/use-step-done'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { StepNode } from '@/api/types'

interface OutlineBasicProps {
  projectId: string
  steps: StepNode[]
}

/**
 * Minimal read-only outline render. Compared to `OutlineEditor`, this surface drops every
 * editing affordance and most chrome; it's designed for skimming the structure of large
 * projects (100, 250, 1000 steps).
 *
 * <p>Behaviors:
 * <ul>
 *   <li><b>Color-coded rows</b> — sections wear a quiet folder palette; actions get a subtle
 *       neutral box so the eye reads them as items to do.
 *   <li><b>Indent guides</b> — vertical rails on the left edge make the tree easy to follow
 *       even on the 1000-step stress project.
 *   <li><b>Local checkboxes on actions</b> — track "I did this" while reading. Persisted per-
 *       project in localStorage; explicitly not synced to the server (see
 *       {@code useStepDone}).
 *   <li><b>Expand / collapse</b> — chevrons on sections show/hide children; chevrons on
 *       actions show/hide a details panel (description, photos). Sections default open;
 *       actions default closed. Collapse-all / Expand-all buttons in the header act on every
 *       row at once.
 * </ul>
 */
export const OutlineBasic = ({ projectId, steps }: OutlineBasicProps) => {
  const expansion = useOutlineExpansion(projectId, steps)
  const done = useStepDone(projectId)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg">Outline</CardTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={expansion.expandAll}
            aria-label="Expand all sections and actions"
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
            Expand all
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={expansion.collapseAll}
            aria-label="Collapse all sections and actions"
          >
            <Minimize2 className="h-3.5 w-3.5" aria-hidden />
            Collapse all
          </Button>
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={expansion.expandAllActions}
            aria-label="Show details on every action"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            Show details
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={expansion.collapseAllActions}
            aria-label="Hide details on every action"
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden />
            Hide details
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {steps.length === 0 ? (
          <p className="text-sm text-muted-foreground">This project has no steps yet.</p>
        ) : (
          steps.map((step, idx) => (
            <OutlineRow
              key={step.id}
              step={step}
              depth={0}
              parentRailsBelow={[]}
              isLastSibling={idx === steps.length - 1}
              expansion={expansion}
              done={done}
            />
          ))
        )}
      </CardContent>
    </Card>
  )
}

/** rem per indent level — matches the legacy `marginLeft: depth * 1.25rem`. */
const INDENT_REM = 1.25
/** Half of CardContent's `space-y-5` (= 1.25rem) so rails extend up into the gap above. */
const GAP_REM = 0.625
/** Where in each indent column the vertical rail lives — small left offset so the rails sit
 *  closer to the start of each column instead of centered. Lower = further left. */
const RAIL_OFFSET_REM = 0.25
/**
 * Vertical offset from the row's top where the elbow horizontal lives — pinned at the
 * checkbox line in the header so it stays put even when the row expands (otherwise the elbow
 * would drift down to the new midpoint when the details panel adds height).
 *
 * <p>Math: pill `py-2` (0.5rem) + half of the `text-sm` line-height (~0.625rem) = 1.125rem.
 */
const ELBOW_TOP_REM = 1.125
/** Rounded corner radius on the L-bend so the elbow doesn't look like a sharp 90° angle. */
const ELBOW_RADIUS_REM = 0.5
/** Stroke width of every rail / elbow line. */
const RAIL_WIDTH = '2px'

interface OutlineRowProps {
  step: StepNode
  depth: number
  /**
   * For each ancestor depth (0..depth-2), does the rail need to keep going below this row?
   * True iff that ancestor has more siblings after the current row's chain.
   */
  parentRailsBelow: boolean[]
  /** Whether this row is the last child of its parent (controls L-bend vs T-junction). */
  isLastSibling: boolean
  expansion: ReturnType<typeof useOutlineExpansion>
  done: ReturnType<typeof useStepDone>
}

const OutlineRow = ({
  step,
  depth,
  parentRailsBelow,
  isLastSibling,
  expansion,
  done,
}: OutlineRowProps) => {
  const isSection = step.kind === 'SECTION'
  const isAction = step.kind === 'ACTION'
  const isProjectRef = step.kind === 'PROJECT_REFERENCE'
  const expanded = expansion.isExpanded(step.id)
  const isDone = isAction && done.isDone(step.id)
  const hasChildren = step.children.length > 0
  const hasDetails =
    isAction && (Boolean(step.description) || (step.photos && step.photos.length > 0))
  const canExpand = (isSection && hasChildren) || hasDetails || isProjectRef

  const titleNode =
    isProjectRef && step.referencedProjectAuthorHandle && step.referencedProjectTitle ? (
      <Link
        to={canonicalProjectUrl(step.referencedProjectAuthorHandle, slugifyTitleHint(step))}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium text-primary hover:underline"
        title={step.title}
      >
        {step.title}
      </Link>
    ) : (
      <span
        className={
          isSection
            ? 'text-sm font-semibold uppercase tracking-wide text-muted-foreground'
            : 'text-sm ' + (isDone ? 'text-muted-foreground line-through' : '')
        }
        title={step.title}
      >
        {step.title}
      </span>
    )

  const milestoneBadge = step.isMilestone ? (
    <span className="inline-flex items-center rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
      Milestone
    </span>
  ) : null

  // Header row content shared between section + action layouts: leading glyph/checkbox,
  // title, milestone badge, then the expand chevron pinned to the far right. The whole row
  // becomes a click target (cursor-pointer + onClick on the outer wrappers below) so users
  // don't have to aim for the chevron specifically; checkbox + link clicks stopPropagation
  // so they still do their own thing without triggering expand.
  // Rails passed down to this row's children. Each child becomes one level deeper, and the
  // chain for its column N-1 (this row's depth) continues iff this row is not the last
  // sibling of its parent.
  const childrenParentRailsBelow = [...parentRailsBelow, !isLastSibling]

  // Section progress — count descendant actions and how many are checked off. Computed
  // every render off the current done-set so toggling an action immediately updates the
  // parent section's progress chip.
  const sectionProgress = isSection ? countActions(step, done) : null
  const sectionAllDone =
    sectionProgress !== null &&
    sectionProgress.total > 0 &&
    sectionProgress.done === sectionProgress.total

  const rails =
    depth > 0 ? (
      <Rails depth={depth} parentRailsBelow={parentRailsBelow} isLastSibling={isLastSibling} />
    ) : null

  const headerRow = (
    <div className="flex items-center gap-2">
      {isAction ? (
        <span onClick={(e) => e.stopPropagation()} className="shrink-0">
          <Checkbox
            checked={isDone}
            onCheckedChange={() => done.toggle(step.id)}
            aria-label={`Mark "${step.title}" ${isDone ? 'not done' : 'done'}`}
          />
        </span>
      ) : isSection ? (
        <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      ) : (
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary/15 text-[10px] font-semibold text-primary">
          ↗
        </span>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {titleNode}
        {milestoneBadge}
      </div>
      {sectionProgress && sectionProgress.total > 0 && (
        <span
          className={
            'inline-flex items-center gap-1.5 text-xs font-medium tabular-nums ' +
            (sectionAllDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground')
          }
          aria-label={`${sectionProgress.done} of ${sectionProgress.total} actions done`}
        >
          <span>
            {sectionProgress.done}/{sectionProgress.total}
          </span>
          {sectionAllDone && (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
            </span>
          )}
        </span>
      )}
      <ExpandChevron canExpand={canExpand} expanded={expanded} />
    </div>
  )

  const onHeaderClick = canExpand ? () => expansion.toggle(step.id) : undefined

  // Section rows: minimal colored block — no border, just background to distinguish from
  // surrounding actions.
  if (isSection) {
    return (
      <div className="space-y-3">
        <div className="relative">
          {rails}
          <div
            className={
              'rounded-md px-3 py-2 transition-colors ' +
              (sectionAllDone ? 'bg-emerald-100/60 dark:bg-emerald-950/30' : 'bg-muted/50') +
              ' ' +
              (canExpand ? 'cursor-pointer select-none' : '')
            }
            onClick={onHeaderClick}
            style={{ marginLeft: `${depth * INDENT_REM}rem` }}
          >
            {headerRow}
          </div>
        </div>
        {hasChildren &&
          expanded &&
          step.children.map((child, idx) => (
            <OutlineRow
              key={child.id}
              step={child}
              depth={depth + 1}
              parentRailsBelow={childrenParentRailsBelow}
              isLastSibling={idx === step.children.length - 1}
              expansion={expansion}
              done={done}
            />
          ))}
      </div>
    )
  }

  // Action + project-reference rows: colored block, no border. Background distinguishes
  // each step; tree rails in the gutter show ancestry.
  return (
    <div className="space-y-3">
      <div className="relative">
        {rails}
        <div
          className={
            'rounded-md px-3 py-2 transition-colors ' +
            rowSurface(step, isDone) +
            (canExpand ? ' cursor-pointer select-none' : '')
          }
          onClick={onHeaderClick}
          style={{ marginLeft: `${depth * INDENT_REM}rem` }}
        >
          {headerRow}
          {/* Details panel inside the pill — same box, separated by a thin divider. */}
          {expanded && hasDetails && (
            <div className="mt-3 flex flex-col gap-3 border-t border-border/60 pt-3 sm:flex-row sm:items-start">
              {step.description && (
                <MarkdownContent
                  value={step.description}
                  className="min-w-0 flex-1 text-sm text-foreground/90"
                />
              )}
              {step.photos && step.photos.length > 0 && (
                <div className="flex shrink-0 flex-wrap gap-2 sm:max-w-[12rem] sm:justify-end">
                  {step.photos.map((photo) => (
                    <a
                      key={photo.id}
                      href={photo.displayUrl ?? photo.thumbUrl ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block h-24 w-24 overflow-hidden rounded-md bg-muted"
                    >
                      {photo.thumbUrl && (
                        <img
                          src={photo.thumbUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface ExpandChevronProps {
  canExpand: boolean
  expanded: boolean
}

/**
 * File-tree style rails — vertical lines per ancestor depth, plus this row's own elbow at
 * the bottom of its depth column. The elbow turns into an L for the last sibling so the
 * branch terminates cleanly; for middle siblings it's a T-junction so the rail keeps going
 * to the next sibling below. Each rail extends up half-a-row-gap into the space above so
 * the chain reads as continuous across the gap between rows.
 */
interface RailsProps {
  depth: number
  parentRailsBelow: boolean[]
  isLastSibling: boolean
}

const Rails = ({ depth, parentRailsBelow, isLastSibling }: RailsProps) => {
  const railColor = 'hsl(var(--border))'
  const railLeft = (depth - 1) * INDENT_REM + RAIL_OFFSET_REM
  // Horizontal connector spans from the rail to the row's left edge.
  const horizontalWidth = INDENT_REM - RAIL_OFFSET_REM
  return (
    <>
      {/* Passing rails for ancestors. */}
      {parentRailsBelow.map((shouldRender, c) =>
        shouldRender ? (
          <span
            key={`rail-${c}`}
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              left: `${c * INDENT_REM + RAIL_OFFSET_REM}rem`,
              top: `-${GAP_REM}rem`,
              bottom: 0,
              borderLeft: `${RAIL_WIDTH} solid ${railColor}`,
            }}
          />
        ) : null
      )}
      {isLastSibling ? (
        /* Last sibling — single span draws the L-bend: border on the left + bottom with a
           rounded inner corner so the turn reads as a smooth curve, not a hard 90°. */
        <span
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            left: `${railLeft}rem`,
            top: `-${GAP_REM}rem`,
            width: `${horizontalWidth}rem`,
            height: `${ELBOW_TOP_REM + GAP_REM}rem`,
            borderLeft: `${RAIL_WIDTH} solid ${railColor}`,
            borderBottom: `${RAIL_WIDTH} solid ${railColor}`,
            borderBottomLeftRadius: `${ELBOW_RADIUS_REM}rem`,
          }}
        />
      ) : (
        <>
          {/* Middle sibling — vertical continues full row + into the gap below. */}
          <span
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              left: `${railLeft}rem`,
              top: `-${GAP_REM}rem`,
              bottom: 0,
              borderLeft: `${RAIL_WIDTH} solid ${railColor}`,
            }}
          />
          {/* Horizontal branch at the elbow line. */}
          <span
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              left: `${railLeft}rem`,
              top: `${ELBOW_TOP_REM}rem`,
              width: `${horizontalWidth}rem`,
              borderTop: `${RAIL_WIDTH} solid ${railColor}`,
            }}
          />
        </>
      )}
    </>
  )
}

/**
 * Passive chevron — the row itself is the click target (see `onHeaderClick`). Keeping this
 * as a non-interactive glyph means clicks anywhere on the row toggle exactly once.
 */
const ExpandChevron = ({ canExpand, expanded }: ExpandChevronProps) => {
  if (!canExpand) return <span className="h-4 w-4 shrink-0" aria-hidden />
  return (
    <span
      aria-hidden
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
    >
      {expanded ? (
        <ChevronDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" />
      )}
    </span>
  )
}

/**
 * Background tint per row. Sections wear a quiet amber-ish folder palette; actions sit on
 * the neutral muted color when not done and fade further when checked. Project references get
 * a primary-tinted nod since they're navigable.
 */
/**
 * Walk a section's subtree and count how many ACTION descendants are marked done. Used to
 * render the per-section progress chip + tint the section row green when everything below
 * it has been checked off.
 */
const countActions = (
  step: StepNode,
  done: ReturnType<typeof useStepDone>
): { total: number; done: number } => {
  let total = 0
  let doneCount = 0
  const walk = (s: StepNode) => {
    if (s.kind === 'ACTION') {
      total++
      if (done.isDone(s.id)) doneCount++
    }
    s.children.forEach(walk)
  }
  step.children.forEach(walk)
  return { total, done: doneCount }
}

/**
 * Background tint for action / project-reference rows. No borders — colors alone distinguish
 * each step. Slightly different shade per kind so the eye reads them differently.
 */
const rowSurface = (step: StepNode, isDone: boolean): string => {
  if (step.kind === 'PROJECT_REFERENCE') return 'bg-primary/10'
  // ACTION — distinct soft blue tint so it reads visually different from the gray sections.
  if (isDone) return 'bg-sky-100/40 dark:bg-sky-950/20 opacity-60'
  return 'bg-sky-100/70 dark:bg-sky-950/40'
}

/** See `outline-basic.tsx` v1 comment — same handle-resolution caveat. */
const slugifyTitleHint = (step: StepNode): string => {
  const source = step.referencedProjectTitle ?? step.title
  return source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
